import React, { useEffect, useState } from "react"
import { AccountFeatures, AppContext } from "@lightspeed-invoicing/core-client/frontend"
import { defineMessages, FormattedMessage, injectIntl } from "react-intl"
import RichTextEditor from "../../common/rich-text/editor.component.jsx"
import EmailSelection from "../../common/email/emailselection.component.jsx"
import EmailTemplateService from "../../state/mail/EmailTemplateService"
import EmailTemplate from "../../state/mail/EmailTemplate"
import { isError } from "../../state/helpers/Error"

type Props = {
    onChange: (object) => void,
    object: string
}

const messages = defineMessages({
    subject_label: {
        id: "common.send_email.modal.subject_label",
        defaultMessage: "Subject",
    },
    from_name_label: {
        id: "common.send_email.modal.from_name_label",
        defaultMessage: "From name",
    },
    from_email_label: {
        id: "common.send_email.modal.from_label",
        defaultMessage: "From",
    },
    from_email_missing_label: {
        id: "common.send_email.modal.from_email_error",
        defaultMessage: "Select one of the emails provided",
    },
    content_label: {
        id: "common.send_email.modal.content_label",
        defaultMessage: "Content",
    },
    button_label_label: {
        id: "common.send_email.modal.button_label",
        defaultMessage: "Button label",
    },
    pdf_include_label: {
        id: "common.send_email.modal.include_pdf_attachment",
        defaultMessage: "Include pdf attachment",
    }
})

const SendBulkEmailComponent = (props: Props) => {
    const [isLoading, setIsLoading] = useState(true)
    const [fromEmail, setFromEmail] = useState("")
    const [content, setContent] = useState("")
    const [sendEmailPayload, setSendEmailPayload] = useState({
        subject: "",
        fromName: "",
        buttonLabel: "",
        pdfIncluded: false,
    })

    useEffect(() => {
        props.onChange({
            ...sendEmailPayload,
            defaultFromEmail: fromEmail,
            content: content,
        })
    }, [sendEmailPayload, fromEmail, content])

    useEffect(() => {
        const templateKey = getTemplateKey()
        EmailTemplateService.get(templateKey).then(res => {
            if (!isError(res)) {
                const emailTemplate = res as EmailTemplate
                setSendEmailPayload({
                    subject: emailTemplate.getSubject(),
                    fromName: emailTemplate.getFromName() || AppContext.getUserLoggedIn().attributes.name,
                    buttonLabel: emailTemplate.getButtonLabel(),
                    pdfIncluded: emailTemplate.dto.attributes.attachPdfByDefault,
                })
                setFromEmail(emailTemplate.getFromEmail())
                setContent(emailTemplate.getContent())
                setIsLoading(false)
            }
        })
    }, [])

    const getTemplateKey = () => {
        switch (props.object) {
        case "invoices":
            return "send-invoice"
        case "orders":
            return "send-order"
        default:
            return ""
        }
    }

    const isPdfFeatureEnabled = () => {
        switch (props.object) {
        case "invoices":
            return AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureIncludePdfAttachmentInInvoice)
        default:
            return false
        }
    }

    if (isLoading) {
        return <></>
    }

    return (
        <div className="modal-body">
            <div className="container-fluid">
                <div className="form-group row mb-1">
                    <label className="col-2 col-form-label custom-form-label pb-2"
                        htmlFor="subject">
                        <FormattedMessage {...messages.subject_label} />
                    </label>
                    <div className="col-10">
                        <input
                            name="subject"
                            id="subject"
                            type="text"
                            value={sendEmailPayload.subject}
                            className="form-control"
                            required
                            onChange={e => {
                                setSendEmailPayload({
                                    ...sendEmailPayload,
                                    subject: e.target.value,
                                })
                            }}
                        />
                    </div>
                </div>

                <div className="form-group row mb-1">
                    <label className="col-2 col-form-label custom-form-label pb-2"
                        htmlFor="fromName">
                        <FormattedMessage {...messages.from_name_label} />
                    </label>
                    <div className="col-10">
                        <input
                            name="fromName"
                            id="fromName"
                            type="text"
                            value={sendEmailPayload.fromName}
                            className="form-control"
                            required
                            onChange={e => {
                                setSendEmailPayload({
                                    ...sendEmailPayload,
                                    fromName: e.target.value,
                                })
                            }}
                        />
                    </div>
                </div>

                <div className="form-group row mb-1">
                    <label className="col-2 col-form-label custom-form-label pb-2"
                        htmlFor="fromEmail">
                        <FormattedMessage {...messages.from_email_label} />
                    </label>
                    <div className="col-10">
                        {!fromEmail && <div className="text-danger">
                            <small>
                                <FormattedMessage {...messages.from_email_missing_label} />
                            </small>
                        </div>}
                        <EmailSelection
                            fromEmail={[fromEmail]}
                            templateKey={getTemplateKey()}
                            onChange={selectedFromEmails => {
                                setFromEmail(selectedFromEmails.length > 0 ? selectedFromEmails[0] : "")
                            }}
                        />
                    </div>
                </div>

                <div className="form-group row mb-0">
                    <div className="col-12">
                        <RichTextEditor
                            height={300}
                            name="content"
                            id="content"
                            value={content}
                            className="form-control"
                            required
                            onChange={value => {
                                setContent(value)
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="form-group row mb-0 d-flex">
                <label className="col-form-label custom-form-label pb-2 px-3 "
                    htmlFor="defaultButton">
                    <FormattedMessage {...messages.button_label_label} />
                </label>
                <div className="ml-2 flex-fill pr-3">
                    <input
                        name="buttonLabel"
                        id="buttonLabel"
                        type="text"
                        value={sendEmailPayload.buttonLabel}
                        className="form-control"
                        onChange={e => {
                            setSendEmailPayload({
                                ...sendEmailPayload,
                                buttonLabel: e.target.value,
                            })
                        }}
                    />
                </div>
            </div>
            {isPdfFeatureEnabled() &&
                <div className={"mb-1 form-group row mt-3"}>
                    <div className="col-12">
                        <div className="custom-control custom-checkbox">
                            <input
                                name="pdfIncluded"
                                type="checkbox"
                                id="pdfIncluded"
                                className="custom-control-input"
                                checked={sendEmailPayload.pdfIncluded}
                                onChange={e => {
                                    setSendEmailPayload({
                                        ...sendEmailPayload,
                                        pdfIncluded: e.target.checked,
                                    })
                                }}
                            />
                            <label className="custom-control-label" htmlFor="pdfIncluded">
                                <FormattedMessage {...messages.pdf_include_label} />
                            </label>
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}

export default injectIntl(SendBulkEmailComponent)




// WEBPACK FOOTER //
// ./src/bulk/operations/SendBulkEmailComponent.tsx